import React, { useEffect, useState } from "react";
import Banner from "./Banner";
import SignupForm from "./SignupForm";
import colors from "../../../utils/colors";
import EmailVerification from "./EmailVerification";
import { IElementObj, ISignupDetails, TKey } from "../../../types";
import { TDoctorInfo } from "../../../apis/types";
import BeAVerifiedDocForm from "./BeAVerifiedDocForm";
import AuthenticationModal from "../AuthenticationModal";
import { forerunnerIcon2, leftArrIcon, verified } from "../../../assets";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useCreateDoctor } from "../../../apis/doctor";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { Flex, Image, useMediaQuery } from "@chakra-ui/react";
import UploadProfilePicForm from "./UploadProfilePicForm";

const RedirectToVerifyLicense = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/signup?tab=verify-license", { replace: true });
  }, [navigate]);

  return <p className="text-center text-lg">Redirecting...</p>;
};

const Signup = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isMobile] = useMediaQuery("(max-width: 767px)");
  const currTab = searchParams?.get("tab") ?? "";
  const [step, setStep] = useState(currTab);
  // State for the email verification success modal
  const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] =
    useState(false);

  const [details, setDetails] = useState<ISignupDetails>({} as ISignupDetails);
  const {
    mutate: createDoctor,
    isPending: createDoctorLoading,
    // data: createdDoctor, // Removed unused variable
  } = useCreateDoctor();
  const { doctorInfo } = useAppSelector((state) => state.auth);

  const updateState = (name: TKey, value: string) => {
    setDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const steps: any = {
    "": [
      "Create your account: !!Strictly for medical personnel",
      "Personal information",
    ],
    "verify-email": ["Verify your account", "Email verification"],
    "upload-profile-pic": ["Add your profile picture", "Profile picture"],
    "verify-license": ["Be a verified doctor", "License verification"],
  };
  const present = {
    "": 1,
    "verify-email": 2,
    "upload-profile-pic": 3,
    "verify-license": 4,
  };

  const docFromSessionString = sessionStorage.getItem("created-doctor");
  let parsedDocFromSession: TDoctorInfo | null = null;
  if (docFromSessionString) {
    try {
      parsedDocFromSession = JSON.parse(docFromSessionString) as TDoctorInfo;
    } catch (e) {
      console.error("Failed to parse doctor info from session storage", e);
    }
  }

  const currentDoctorData: TDoctorInfo | undefined =
    doctorInfo && doctorInfo._id
      ? (doctorInfo as TDoctorInfo)
      : parsedDocFromSession || undefined;

  const formToDisplay: IElementObj = {
    "": (
      <SignupForm
        setStep={setStep}
        setSearchParams={setSearchParams}
        details={details}
        setDetails={setDetails}
        createDoctor={createDoctor}
        createDoctorLoading={createDoctorLoading}
        updateState={updateState}
      />
    ),
    "verify-email": (
      <EmailVerification
        setStep={setStep}
        setSearchParams={setSearchParams}
        doctor={currentDoctorData}
        setModalOpen={setIsEmailVerificationModalOpen}
      />
    ),
    // "upload-profile-pic": (
    //   <UploadProfilePicForm
    //     doctor={currentDoctorData}
    //     setStep={setStep}
    //     setSearchParams={setSearchParams}
    //   />
    // ),
    "upload-profile-pic": <RedirectToVerifyLicense />,

    "verify-license": <BeAVerifiedDocForm doctor={currentDoctorData} />,
  };

  useEffect(() => {
    const typedDoctorInfoFromRedux = doctorInfo as TDoctorInfo | null;

    if (typedDoctorInfoFromRedux && typedDoctorInfoFromRedux._id) {
      if (typedDoctorInfoFromRedux.isVerified === true) {
        navigate("/");
        return;
      }
    } else if (
      (currTab === "verify-email" ||
        currTab === "upload-profile-pic" ||
        currTab === "verify-license") &&
      !docFromSessionString
    ) {
      navigate("/signup/");
      setStep("");
      return;
    } else if (
      currTab === "upload-profile-pic" &&
      currentDoctorData?.profilePicture
    ) {
      // ✅ Already has a picture, skip to next step
      setStep("verify-license");
      setSearchParams({ tab: "verify-license" });
      return;
    }

    setStep(currTab);
  }, [currTab, docFromSessionString, doctorInfo, navigate]);

  const receivedValue = location.state?.step || "";
  return (
    <>
      <AuthenticationModal
        btnText="Proceed"
        headerText="Email Verified"
        image={verified}
        isOpen={isEmailVerificationModalOpen}
        onClose={() => setIsEmailVerificationModalOpen(false)}
        subText="Your email has been verified successfully."
        title=""
        onclickProceed={() => {
          setIsEmailVerificationModalOpen(false);
          setStep("upload-profile-pic");
          setSearchParams({ tab: "upload-profile-pic" });
        }}
      />
      {isMobile && (
        <Flex justifyContent={"center"}>
          <Image
            src={forerunnerIcon2}
            boxSize={"48px"}
            mt={"20px"}
            alt="Ubimed Logo Mobile"
          />
        </Flex>
      )}
      <div className="flex">
        {!isMobile && <Banner />}
        <div className="flex flex-col flex-1 gap-[20px] md:gap-[40px] items-center py-[5%] px-[5%] md:px-[7%]">
          <div
            className="flex gap-2 cursor-pointer w-[100%] md:ml-[-80px]"
            style={{ color: colors.dark }}
            onClick={() => {
              navigate(-1);
            }}
          >
            <img src={leftArrIcon} alt="Back arrow" />
            <p
              className="text-[14px] md:text-[16px] font-[700] leading-normal"
              style={{ color: colors.dark }}
            >
              Back
            </p>
          </div>

          <div
            className="flex flex-col w-full gap-[20px]"
            style={{ color: colors.dark }}
          >
            <div className="font-lexend text-[20px] md:text-[32px] font-[600] leading-[40px]">
              {steps[step]?.[0] || "Complete Signup"}
            </div>
            <div className="font-lato text-[16px] md:text-[20px] font-[400]">
              {`Step ${present[step] || present[""]} of 4: ${
                steps[step]?.[1] || "Please complete the steps"
              }`}
            </div>
          </div>
          {formToDisplay[step || receivedValue]}
        </div>
      </div>
    </>
  );
};

export default Signup;
